// 효과 탭 - 보스 효과 목록 관리
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  message,
  Popconfirm,
  Typography,
  Input,
  Modal,
  Select,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { useApp } from "../../contexts/AppContext";
import { effectReferences } from "../../utils/resourcePresentation";
import { raidAuthoringApi } from "../../services/raidAuthoringApi";
import "./ResourceTables.css";
import {
  effectApi,
  skillApi,
  referenceApi,
  improvedTriggerApi,
} from "../../services/api";
import { supabase } from "../../lib/supabase";
import { BossEffect } from "../../types";
import ImageUploadComponent from "../ImageUpload/ImageUploadComponent";
import "../ImageUpload/ImageUpload.css";

const { Text, Title } = Typography;

interface EffectsTabProps {
  isCommonMode?: boolean;
  bossCode?: string;
  entityCode?: string;
  onUpdate?: () => void;
}

interface EffectWithImage extends BossEffect {
  image_url?: string | null;
  image_uploaded_at?: string | null;
}

const EffectsTab: React.FC<EffectsTabProps> = ({
  isCommonMode = false,
  bossCode,
  entityCode,
  onUpdate,
}) => {
  const { state, openEffectModal, openCopyModal } = useApp();
  const [effects, setEffects] = useState<EffectWithImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [scope, setScope] = useState("all");
  const [loadError, setLoadError] = useState("");
  const [referencedEffects, setReferencedEffects] = useState<Set<string>>(
    new Set(),
  );

  const [skillEffectRelations, setSkillEffectRelations] = useState<any[]>([]);

  // 필요한 참조 데이터
  const [effectAffects, setEffectAffects] = useState<any[]>([]);
  const [stats, setStats] = useState<any[]>([]);
  const [effectTypes, setEffectTypes] = useState<any[]>([]);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedEffectForImage, setSelectedEffectForImage] =
    useState<EffectWithImage | null>(null);

  const loadRequest = useRef(0);
  // 현재 보스 코드
  const currentBossCode = isCommonMode
    ? "COMMON"
    : entityCode ||
      bossCode ||
      state.selectedEntity?.entity_code ||
      state.selectedBoss?.boss_code;

  const loadReferenceData = useCallback(async () => {
    try {
      const [effectAffectData, statData, effectTypeData] = await Promise.all([
        referenceApi.fetchEffectAffects(),
        referenceApi.fetchStats(),
        referenceApi.fetchEffectTypes(),
      ]);

      setEffectAffects(effectAffectData);
      setStats(statData);
      setEffectTypes(effectTypeData);
    } catch (error) {
      console.error("Failed to load reference data:", error);
    }
  }, []);

  const loadEffects = useCallback(async () => {
    if (!currentBossCode) return;
    const requestId = ++loadRequest.current;
    setLoading(true);
    setLoadError("");
    try {
      const childrenResult = await supabase
        .from("raid_entity_renewed")
        .select("entity_code,entity_name")
        .eq("parent_code", currentBossCode);
      if (childrenResult.error) throw childrenResult.error;
      const children = entityCode ? [] : childrenResult.data || [];
      const codes = [currentBossCode, ...children.map((c) => c.entity_code)];
      const effectCodes = Array.from(new Set([...codes, "COMMON"]));
      const [effectLists, skillLists, links, legacyLists] = await Promise.all([
        Promise.all(effectCodes.map((c) => effectApi.getByBoss(c))),
        Promise.all(codes.map((c) => skillApi.getByBoss(c))),
        raidAuthoringApi.triggers(codes),
        Promise.all(
          codes.map((c) => improvedTriggerApi.getTriggerActionsByBoss(c)),
        ),
      ]);
      const skillKeys = Array.from(
        new Set(skillLists.flat().map((s) => s.key)),
      );
      const relations = skillKeys.length
        ? await supabase
            .from("boss_skill_effect_relation_renewed")
            .select("*")
            .in("skill", skillKeys)
        : { data: [], error: null };
      if (relations.error) throw relations.error;
      const groups = links.triggers.length
        ? await supabase
            .from("boss_trigger_condition_group_v3")
            .select("id")
            .in(
              "trigger_id",
              links.triggers.map((t) => t.id),
            )
        : { data: [], error: null };
      if (groups.error) throw groups.error;
      const conditions = groups.data?.length
        ? await supabase
            .from("boss_trigger_condition_v3")
            .select("*")
            .in(
              "group_id",
              groups.data.map((g) => g.id),
            )
        : { data: [], error: null };
      if (conditions.error) throw conditions.error;
      const allEffects = Array.from(
        new Map(effectLists.flat().map((e) => [e.key, e])).values(),
      ).map((e) => ({
        ...e,
        _parent_name: children.find((c) => c.entity_code === e.boss_code)
          ?.entity_name,
      }));
      if (requestId !== loadRequest.current) return;
      setEffects(allEffects);
      setSkillEffectRelations(relations.data || []);
      setReferencedEffects(
        effectReferences(
          [...legacyLists.flat(), ...links.actions],
          conditions.data || [],
        ),
      );
    } catch (error: any) {
      if (requestId === loadRequest.current)
        setLoadError(error.message || "효과 연결 정보를 불러오지 못했습니다.");
    } finally {
      if (requestId === loadRequest.current) setLoading(false);
    }
  }, [currentBossCode, entityCode]);
  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);
  useEffect(() => {
    setScope("all");
    loadEffects();
    const refresh = () => {
      loadEffects();
    };
    const events = ["skillsUpdated", "effectsUpdated", "triggersUpdated"];
    events.forEach((name) => window.addEventListener(name, refresh));
    return () => {
      loadRequest.current++;
      events.forEach((name) => window.removeEventListener(name, refresh));
    };
  }, [loadEffects]);

  // 효과 삭제
  const handleDelete = async (effectKey: string) => {
    try {
      await effectApi.delete(effectKey);
      message.success("효과가 삭제되었습니다.");
      loadEffects();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("효과 삭제 실패:", error);
      message.error("효과 삭제에 실패했습니다.");
    }
  };

  // 참조 데이터에서 레이블 가져오기

  const getEffectAffect = (code: string) => {
    const affect = effectAffects.find((a) => a.affect_code === code);
    return affect?.affect_name || code;
  };

  const getStatCode = (code: string) => {
    const stat = stats.find((s) => s.stat_code === code);
    return stat?.stat_name || code;
  };

  const handleImageUpload = (effect: EffectWithImage) => {
    setSelectedEffectForImage(effect);
    setImageModalVisible(true);
  };

  const handleImageUploadSuccess = (imageUrl: string) => {
    message.success("이미지가 업로드되었습니다.");
    loadEffects(); // 목록 새로고침
  };

  const handleImageDeleteSuccess = () => {
    message.success("이미지가 삭제되었습니다.");
    loadEffects(); // 목록 새로고침
  };

  const linkedSkills = (key: string) =>
    Array.from(
      new Set(
        skillEffectRelations
          .filter((r) => r.effect === key)
          .map((r) => r.skill),
      ),
    );
  const filtered = effects
    .filter(
      (e) =>
        (scope === "all" ||
          (scope === "common"
            ? e.boss_code === "COMMON"
            : e.boss_code !== "COMMON")) &&
        [e.name, e.key, e.description, (e as any)._parent_name].some((v) =>
          v?.toLowerCase().includes(searchText.toLowerCase()),
        ),
    )
    .sort(
      (a, b) =>
        Number(a.boss_code === "COMMON") - Number(b.boss_code === "COMMON"),
    );
  const columns: ColumnsType<EffectWithImage> = [
    {
      title: "효과",
      key: "name",
      width: 260,
      render: (_, e) => (
        <div className="resource-name">
          <Text strong>{e.name}</Text>
          <Text type="secondary">
            {e.boss_code === "COMMON"
              ? "공통"
              : (e as any)._parent_name || "본체"}
          </Text>
        </div>
      ),
    },
    {
      title: "유형",
      dataIndex: "cat",
      width: 110,
      render: (cat) => (
        <Tag>
          {effectTypes.find((t) => t.effect_code === cat)?.effect_name ||
            (
              {
                BUFF: "버프",
                DEBUFF: "디버프",
                DAMAGE: "피해",
                HEAL: "회복",
              } as any
            )[cat] ||
            cat ||
            "미설정"}
        </Tag>
      ),
    },
    {
      title: "적용 대상",
      dataIndex: "affect",
      width: 130,
      render: getEffectAffect,
    },
    {
      title: "최대 중첩",
      dataIndex: "max_count",
      width: 100,
      render: (n) => (n == null ? "미설정" : n < 0 ? "무제한" : n + "회"),
    },
    {
      title: "연결",
      key: "usage",
      width: 180,
      render: (_, e) =>
        loadError ? (
          <Text type="warning">확인 실패</Text>
        ) : (
          <Space direction="vertical" size={0}>
            <Text>스킬 {linkedSkills(e.key).length}개</Text>
            <Text type="secondary">
              {referencedEffects.has(e.key)
                ? "트리거에서 참조"
                : "현재 범위의 트리거 참조 없음"}
            </Text>
          </Space>
        ),
    },
    {
      title: "작업",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="편집">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                openEffectModal(record);
                if (onUpdate) onUpdate();
              }}
            />
          </Tooltip>
          <Tooltip title="복사">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => openCopyModal("effect", record, record.name)}
            />
          </Tooltip>
          <Popconfirm
            title="삭제 확인"
            description="이 효과를 삭제하시겠습니까?"
            onConfirm={() => handleDelete(record.key)}
            okText="삭제"
            cancelText="취소"
          >
            <Tooltip title="삭제">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];
  return (
    <div className="resource-page">
      <div className="resource-toolbar">
        <div>
          <Title level={4}>{isCommonMode ? "공통 효과" : "효과"}</Title>
          <Text type="secondary">
            전용 효과를 먼저 표시합니다. 사용 연결은 현재 몬스터 범위를 기준으로
            확인합니다.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openEffectModal()}
        >
          효과 추가
        </Button>
      </div>
      <Space wrap className="resource-filters">
        <Input.Search
          aria-label="효과 검색"
          placeholder="이름, 코드 또는 설명 검색"
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 280 }}
        />
        {!isCommonMode && (
          <Select
            aria-label="효과 범위"
            value={scope}
            onChange={setScope}
            style={{ width: 160 }}
            options={[
              { value: "all", label: "전용 + 공통" },
              { value: "own", label: "전용 효과" },
              { value: "common", label: "공통 효과" },
            ]}
          />
        )}
      </Space>
      {loadError && (
        <Alert
          type="error"
          showIcon
          message={loadError}
          action={<Button onClick={loadEffects}>다시 시도</Button>}
        />
      )}
      <Table
        className="resource-table"
        columns={columns}
        dataSource={filtered}
        rowKey="key"
        loading={loading}
        scroll={{ x: 950 }}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          showTotal: (n) => n + "개",
        }}
        expandable={{
          expandedRowRender: (e) => (
            <div className="resource-details">
              <Text code copyable>
                {e.key}
              </Text>
              <p>{e.description || "설명 없음"}</p>
              <Space wrap>
                <Tag>계산 {e.operator || "미설정"}</Tag>
                <Tag>입력 능력치 {getStatCode(e.in_stat) || "없음"}</Tag>
                <Tag>출력 능력치 {getStatCode(e.out_stat) || "없음"}</Tag>
                <Tag>적용 주기 {e.tick_period_s ?? "미설정"}</Tag>
              </Space>
              <p>
                <Text strong>연결된 스킬</Text>
              </p>
              <Space wrap>
                {linkedSkills(e.key).map((key) => (
                  <Tag key={key}>{key}</Tag>
                ))}
                {!linkedSkills(e.key).length && (
                  <Text type="secondary">
                    현재 범위에서 연결된 스킬이 없습니다.
                  </Text>
                )}
              </Space>
              <p>
                <Button
                  icon={<PictureOutlined />}
                  onClick={() => handleImageUpload(e)}
                >
                  효과 아이콘 관리
                </Button>
              </p>
            </div>
          ),
        }}
      />
      {/* 이미지 업로드 모달 */}
      <Modal
        title={`효과 아이콘 - ${selectedEffectForImage?.name}`}
        open={imageModalVisible}
        onCancel={() => {
          setImageModalVisible(false);
          setSelectedEffectForImage(null);
        }}
        footer={null}
        width={400}
      >
        {selectedEffectForImage && (
          <ImageUploadComponent
            entityType="effect"
            entityId={selectedEffectForImage.key}
            entityName={selectedEffectForImage.name}
            currentImageUrl={selectedEffectForImage.image_url || undefined}
            onUploadSuccess={handleImageUploadSuccess}
            onDeleteSuccess={handleImageDeleteSuccess}
          />
        )}
      </Modal>
    </div>
  );
};
export default EffectsTab;
